import React from 'react'
import {
  IoGridOutline,
  IoCalendarOutline,
  IoHammerOutline,
  IoReceiptOutline,
  IoSettingsOutline,
  IoTrendingUpOutline,
  IoFlashOutline,
  IoMapOutline,
  IoRefresh,
  IoPersonOutline,
  IoAnalytics,
  IoShieldOutline,
  IoCardOutline,
  IoFootballOutline,
  IoStatsChartOutline,
  IoFilterOutline,
  IoPulseOutline,
  IoTrophyOutline,
} from 'react-icons/io5'
import { useBetBuilder, useBetTracker } from '../context/BetContext'
import { useTheme } from '../context/ThemeContext'
import { useLiveTipsNotification } from '../context/LiveTipsContext'
import { useSidebar } from '../context/SidebarContext'
import { useLeague, isLiveTodayMode } from '../context/LeagueContext'
import { useIsAdmin } from '../hooks'
import type { PageId } from './BottomNav'

interface NavItem {
  id: string
  label: string
  pageId?: PageId
  icon: React.ComponentType<{ size?: number; color?: string }>
  badge?: 'count' | 'dot'
  action?: 'page' | 'modal'
  adminOnly?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', pageId: 'dashboard', icon: IoGridOutline },
  { id: 'leagues', label: 'Leagues', icon: IoTrophyOutline, action: 'page' },
  { id: 'fixtures', label: 'Fixtures', pageId: 'fixtures', icon: IoCalendarOutline },
  { id: 'cardshub', label: 'Cards Hub', pageId: 'cardshub', icon: IoCardOutline },
  { id: 'goalshub', label: 'Goals Hub', pageId: 'goalshub', icon: IoStatsChartOutline },
  { id: 'dixoncoles', label: 'Dixon-Coles', pageId: 'dixoncoles', icon: IoPulseOutline },
  { id: 'aipredictions', label: 'AI Predictions', pageId: 'aipredictions', icon: IoAnalytics },
  { id: 'valuebets', label: 'Value Bets', pageId: 'valuebets', icon: IoTrendingUpOutline },
  { id: 'strategies', label: 'Strategies', pageId: 'strategies', icon: IoFilterOutline },
  { id: 'livetips', label: 'Live Tips', pageId: 'livetips', icon: IoFlashOutline, badge: 'dot' },
  { id: 'referees', label: 'Referees', pageId: 'referees', icon: IoPersonOutline },
  { id: 'playerstats', label: 'Player Stats', pageId: 'playerstats', icon: IoFootballOutline },
  { id: 'builder', label: 'Builder', icon: IoHammerOutline, badge: 'count', action: 'modal' },
  { id: 'mybets', label: 'My Bets', pageId: 'mybets', icon: IoReceiptOutline, badge: 'count' },
  { id: 'roadmap', label: 'Roadmap', pageId: 'roadmap', icon: IoMapOutline },
  { id: 'admin', label: 'Admin', pageId: 'admin', icon: IoShieldOutline, adminOnly: true },
]

interface SidebarNavProps {
  activePage: PageId
  onPageChange: (page: PageId) => void
  onLeagueClick?: (apiLeagueId: number) => void
  onRefresh?: () => Promise<void>
  isRefreshing?: boolean
}

export function SidebarNav({ activePage, onPageChange, onLeagueClick, onRefresh, isRefreshing }: SidebarNavProps) {
  const { bets, openModal } = useBetBuilder()
  const { getPendingCount } = useBetTracker()
  const { openSettings } = useTheme()
  const { hasNewTips, markTipsSeen } = useLiveTipsNotification()
  const { closeMobileMenu, isCollapsed } = useSidebar()
  const { currentLeague } = useLeague()
  const { isAdmin } = useIsAdmin()

  const pendingCount = getPendingCount()

  // Filter nav items based on admin status and hide leagues if in Live Today mode
  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (item.adminOnly && !isAdmin) return false
    // Hide leagues nav item if in Live Today mode (no specific league selected)
    if (item.id === 'leagues' && isLiveTodayMode(currentLeague)) return false
    return true
  })

  const handleClick = (item: NavItem) => {
    if (item.id === 'builder') {
      openModal()
      closeMobileMenu()
    } else if (item.id === 'leagues') {
      // Navigate to league page for current league
      if (onLeagueClick && currentLeague && !isLiveTodayMode(currentLeague)) {
        onLeagueClick(currentLeague.api_league_id)
        closeMobileMenu()
      }
    } else if (item.pageId) {
      if (item.pageId === 'livetips') {
        markTipsSeen()
      }
      onPageChange(item.pageId)
      closeMobileMenu()
    }
  }

  const handleSettingsClick = () => {
    openSettings()
    closeMobileMenu()
  }

  const handleRefreshClick = async () => {
    if (onRefresh && !isRefreshing) {
      await onRefresh()
    }
  }

  const renderBadge = (item: NavItem) => {
    if (item.id === 'builder' && bets.length > 0) {
      return <span className="sidebar-item-badge">{bets.length}</span>
    }
    if (item.id === 'mybets' && pendingCount > 0) {
      return <span className="sidebar-item-badge">{pendingCount}</span>
    }
    if (item.id === 'livetips' && hasNewTips && activePage !== 'livetips') {
      return <span className="sidebar-item-dot" />
    }
    return null
  }

  // Check if item is active
  const isItemActive = (item: NavItem) => {
    if (item.id === 'leagues') {
      return activePage === 'league'
    }
    return item.pageId === activePage
  }

  return (
    <nav className="sidebar-nav">
      <div className="sidebar-section-label">Navigation</div>
      {visibleNavItems.map((item) => (
        <button
          type="button"
          key={item.id}
          className={`sidebar-item ${isItemActive(item) ? 'active' : ''}`}
          onClick={() => handleClick(item)}
          data-tooltip={isCollapsed ? item.label : undefined}
        >
          {renderBadge(item)}
          <span className="sidebar-item-icon">
            <item.icon size={20} />
          </span>
          <span className="sidebar-item-label">{item.label}</span>
        </button>
      ))}

      <div className="sidebar-divider" />

      {/* Settings */}
      <button
        type="button"
        className="sidebar-item"
        onClick={handleSettingsClick}
        data-tooltip={isCollapsed ? 'Settings' : undefined}
      >
        <span className="sidebar-item-icon">
          <IoSettingsOutline size={20} />
        </span>
        <span className="sidebar-item-label">Settings</span>
      </button>

      {/* Refresh */}
      {onRefresh && (
        <button
          type="button"
          className={`sidebar-refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
          onClick={handleRefreshClick}
          disabled={isRefreshing}
          data-tooltip={isCollapsed ? 'Refresh' : undefined}
        >
          <span className="sidebar-item-icon">
            <IoRefresh size={20} />
          </span>
          <span className="sidebar-item-label">Refresh</span>
        </button>
      )}
    </nav>
  )
}
